// Helpers for the Gateway FAB transcript: detect and summarize JSON blobs,
// extract citations, pick mini-table columns, and parse out external
// untrusted-content quarantine blocks. Kept side-effect-free so the panel
// stays easy to reason about.

const QUARANTINE_OPEN_RE =
  /<<<EXTERNAL_UNTRUSTED_CONTENT(?:\s+id="([^"]*)")?>>>/;
const QUARANTINE_CLOSE_RE =
  /<<<END_EXTERNAL_UNTRUSTED_CONTENT(?:\s+id="([^"]*)")?>>>/;

const NAME_KEYS = [
  'display_name',
  'name',
  'title',
  'label',
  'summary',
  'first_name',
];
const SECONDARY_KEYS = [
  'email',
  'company',
  'status',
  'role',
  'type',
  'project',
  'priority',
  'kind',
  'url',
];

/** Try to parse a JSON value out of a text segment. Returns null on failure. */
export function tryParseJson(text) {
  if (typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (!trimmed) return null;
  const head = trimmed[0];
  if (head !== '{' && head !== '[') return null;
  try {
    const value = JSON.parse(trimmed);
    if (value === null || typeof value !== 'object') return null;
    return value;
  } catch {
    return null;
  }
}

/** Produce a short, verb-first summary for a JSON value. */
export function summarizeJson(value) {
  if (Array.isArray(value)) {
    const sampleKeys = arrayOfObjects(value)
      ? Object.keys(value[0] || {}).slice(0, 3).join(', ')
      : '';
    return `[${value.length} item${value.length === 1 ? '' : 's'}]${sampleKeys ? ` · ${sampleKeys}` : ''}`;
  }
  if (value && typeof value === 'object') {
    if (typeof value.command === 'string') {
      const cmd = value.command.split(/\r?\n/)[0].trim();
      return `$ ${cmd.length > 80 ? cmd.slice(0, 79) + '…' : cmd}`;
    }
    if (typeof value.query === 'string') {
      const provider = value.provider ? ` · ${value.provider}` : '';
      return `🔎 "${value.query}"${provider}`;
    }
    if (typeof value.name === 'string' && value.member_count != null) {
      return `${value.name} · ${value.member_count} member${value.member_count === 1 ? '' : 's'}`;
    }
    if (typeof value.error === 'string') return `⚠ ${value.error}`;
    const keys = Object.keys(value).slice(0, 4);
    return keys.join(', ');
  }
  return String(value).slice(0, 60);
}

/** Map a tool name to an icon key + accent class. The icon key resolves to
 *  an inline SVG in gatewayParts' ToolGlyph — no emoji. */
export function toolNameTaxonomy(name) {
  const n = String(name || '').toLowerCase();
  if (n.startsWith('hub') || n.includes('command') || n === 'bash' || n === 'shell') {
    return { icon: 'terminal', family: 'hub' };
  }
  if (n.includes('search') || n.includes('web')) {
    return { icon: 'globe', family: 'web' };
  }
  if (n.includes('fetch') || n.includes('http') || n.includes('curl')) {
    return { icon: 'download-cloud', family: 'web' };
  }
  if (n.includes('write') || n.includes('edit') || n.includes('save')) {
    return { icon: 'edit', family: 'write' };
  }
  if (n.includes('read') || n.includes('open') || n.includes('show')) {
    return { icon: 'eye', family: 'read' };
  }
  return { icon: 'tool', family: 'generic' };
}

/** Infer a synthetic tool name from a bare JSON tool-call shape. */
export function inferToolNameFromValue(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  if (typeof value.command === 'string') {
    const head = value.command.split(/\s+/)[0] || '';
    return head ? head.toLowerCase() : 'shell';
  }
  if (typeof value.query === 'string') return 'web_search';
  return null;
}

/**
 * Pull the actual shell command out of an exec-style tool call so it can be
 * rendered as a terminal line instead of a raw JSON envelope. Accepts either
 * an already-parsed args object (`{ command: "…" }`) or the raw args text
 * (which may itself be a JSON string carrying `command`). Returns the trimmed
 * command string, or null when this is not an exec/shell call.
 */
export function extractShellCommand(args, argsText) {
  const fromObj = (v) => (v && typeof v === 'object' && !Array.isArray(v) && typeof v.command === 'string')
    ? v.command
    : null;
  let cmd = fromObj(args);
  if (cmd == null && typeof argsText === 'string' && argsText.trim()) {
    const parsed = tryParseJson(argsText);
    cmd = fromObj(parsed);
    // A bare non-JSON args string that already looks like a command is
    // treated as one only when the tool name says so; callers pass the
    // parsed object path first, so this stays conservative.
  }
  if (typeof cmd !== 'string') return null;
  const trimmed = cmd.replace(/\s+$/, '');
  return trimmed.length ? trimmed : null;
}

/** True when an array is a non-empty list of plain objects sharing structure. */
function arrayOfObjects(value) {
  if (!Array.isArray(value) || value.length < 1) return false;
  let firstKeys = null;
  for (const item of value) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
    const keys = Object.keys(item);
    if (firstKeys == null) firstKeys = new Set(keys);
    else {
      // accept loose superset overlap; we only need a few stable keys for the table
      let overlap = 0;
      for (const k of keys) if (firstKeys.has(k)) overlap++;
      if (overlap < 2) return false;
    }
  }
  return true;
}

/** True when this array shape is a good fit for a mini-table preview. */
export function isMiniTableShape(value) {
  return arrayOfObjects(value) && value.length >= 1;
}

/** Pick at most three labelly column keys for a mini table. */
export function pickTableColumns(items) {
  if (!Array.isArray(items) || !items.length) return [];
  const counts = new Map();
  for (const item of items) {
    for (const k of Object.keys(item || {})) {
      counts.set(k, (counts.get(k) || 0) + 1);
    }
  }
  const present = (k) => counts.has(k);
  const cols = [];
  if (present('id')) cols.push('id');
  for (const k of NAME_KEYS) {
    if (present(k) && !cols.includes(k)) { cols.push(k); break; }
  }
  for (const k of SECONDARY_KEYS) {
    if (cols.length >= 3) break;
    if (present(k) && !cols.includes(k)) cols.push(k);
  }
  if (cols.length < 3) {
    // pad with whatever keys are most common (excluding noisy long ones)
    const ranked = [...counts.entries()]
      .filter(([k]) => !cols.includes(k) && !/^(created|updated|labels|relationships|history)$/i.test(k))
      .sort((a, b) => b[1] - a[1])
      .map(([k]) => k);
    for (const k of ranked) {
      if (cols.length >= 3) break;
      cols.push(k);
    }
  }
  return cols.slice(0, 3);
}

/** Pull out citation chips from a tool-result JSON if present. Returns []. */
export function extractCitations(value) {
  if (!value || typeof value !== 'object') return [];
  const list = Array.isArray(value.citations) ? value.citations : null;
  if (!list) return [];
  return list
    .map((c) => {
      if (!c || typeof c !== 'object') return null;
      const url = typeof c.url === 'string' ? c.url : null;
      if (!url) return null;
      // Drop noisy Google grounding redirects.
      if (url.includes('vertexaisearch.cloud.google.com/grounding-api-redirect')) return null;
      const title = typeof c.title === 'string' && c.title.trim()
        ? c.title.trim()
        : domainFromUrl(url);
      return { url, title };
    })
    .filter(Boolean);
}

function domainFromUrl(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/**
 * Split a text body into intro/quarantine-blocks/outro so each external
 * untrusted block can render inside a labeled card. Returns null if the
 * text contains no recognisable quarantine markers.
 */
export function splitExternalQuarantine(text) {
  if (typeof text !== 'string') return null;
  if (!QUARANTINE_OPEN_RE.test(text)) return null;
  const out = [];
  let cursor = 0;
  while (cursor < text.length) {
    const remain = text.slice(cursor);
    const openMatch = remain.match(QUARANTINE_OPEN_RE);
    if (!openMatch) {
      const tail = remain.trim();
      if (tail) out.push({ kind: 'text', text: remain });
      break;
    }
    const openIdx = cursor + openMatch.index;
    if (openIdx > cursor) {
      out.push({ kind: 'text', text: text.slice(cursor, openIdx) });
    }
    const id = openMatch[1] || '';
    const afterOpen = openIdx + openMatch[0].length;
    const tail = text.slice(afterOpen);
    const closeMatch = tail.match(QUARANTINE_CLOSE_RE);
    if (!closeMatch) {
      out.push({ kind: 'quarantine', id, body: tail });
      cursor = text.length;
      break;
    }
    const body = tail.slice(0, closeMatch.index);
    const { source, content } = parseQuarantineBody(body);
    out.push({ kind: 'quarantine', id, source, body: content });
    cursor = afterOpen + closeMatch.index + closeMatch[0].length;
  }
  // If everything coalesced to a single text chunk, skip the wrapping work.
  if (out.every((p) => p.kind === 'text')) return null;
  return out;
}

function parseQuarantineBody(body) {
  const lines = String(body).split(/\r?\n/);
  let source = null;
  let i = 0;
  // Skip leading blanks
  while (i < lines.length && !lines[i].trim()) i++;
  if (i < lines.length && /^Source:\s*/i.test(lines[i])) {
    source = lines[i].replace(/^Source:\s*/i, '').trim();
    i++;
  }
  // Skip a single divider line if present (`---`)
  while (i < lines.length && /^\s*(?:-{3,}|=+|_{3,})\s*$/.test(lines[i])) i++;
  while (i < lines.length && !lines[i].trim()) i++;
  return { source, content: lines.slice(i).join('\n').trim() };
}

/** Cap a string at N chars on a word boundary if possible, with ellipsis. */
export function truncateAt(text, max) {
  const s = String(text || '');
  if (s.length <= max) return s;
  const slice = s.slice(0, max);
  const lastBreak = slice.lastIndexOf('\n');
  if (lastBreak > max * 0.5) return slice.slice(0, lastBreak) + '\n…';
  const lastSpace = slice.lastIndexOf(' ');
  if (lastSpace > max * 0.6) return slice.slice(0, lastSpace) + '…';
  return slice + '…';
}
